import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import enCommon from './locales/en/common.json';
import type { InitI18nOptions, SupportedLanguage } from './type';

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * Resource bundles, namespaced by feature.
 *
 * `common` holds copy that is genuinely shared - actions, empty/error states,
 * pagination. A feature gets its own namespace only once it has enough strings to
 * warrant one; until then its keys live under `common`. Add a language by adding a
 * sibling folder under `locales/`, never by scattering JSON into features or apps.
 */
const resources = {
  en: {
    common: enCommon,
  },
} as const;

/**
 * Initialises the shared i18next instance. Each app calls this once in `main.tsx`
 * before rendering; `vitest.setup.ts` calls it too, so a component under test
 * renders real copy rather than raw keys.
 *
 * Calling it more than once is a no-op rather than an error - `initReactI18next`
 * throws on a double init, and a hot reload would otherwise take the app down.
 */
export const initI18n = (options: InitI18nOptions = {}): I18nInstance => {
  if (i18next.isInitialized) return i18next;

  void i18next.use(initReactI18next).init({
    resources,
    lng: options.language ?? DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: 'common',
    ns: ['common'],
    interpolation: {
      // React already escapes everything it renders; escaping again turns an
      // apostrophe in interpolated data into `&#39;` on screen.
      escapeValue: false,
    },
    returnNull: false,
  });

  return i18next;
};

export { i18next };
